import { useQuery } from '@tanstack/react-query';
import type { PostgrestError } from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';

export const authenticatedBookingReadinessKeys = {
  all: ['authenticated-booking-readiness'] as const,
  detail: (userId?: string | null) => [...authenticatedBookingReadinessKeys.all, userId ?? 'anonymous'] as const,
};

export type AuthenticatedBookingMissingRequirement =
  | 'tenant_access_required'
  | 'confirmed_email_required'
  | 'display_name_required'
  | 'verified_phone_required'
  | 'crm_identity_conflict'
  | string;

export interface AuthenticatedBookingReadiness {
  actorAccess: {
    roleCode: string | null;
    accessStatus: string | null;
    canAccess: boolean;
    isPublicTenantActor: boolean;
  };
  identity: {
    displayName: string | null;
    canonicalEmail: string | null;
    emailConfirmed: boolean;
    verifiedPhone: string | null;
    bookingProfileSufficient: boolean;
  };
  crmIdentity: {
    candidateClientId: string | null;
    candidateClientPublicId: string | null;
    resolutionStatus: string | null;
    resolutionStrategy: string | null;
    activeLinkId: string | null;
    matchedEmailClientId: string | null;
    matchedVerifiedPhoneClientId: string | null;
    matchedEmailCount: number;
    matchedVerifiedPhoneCount: number;
    canLinkDeterministically: boolean;
    canCreateOnConfirm: boolean;
    isAmbiguous: boolean;
  };
  sourcePrompt: {
    state: string | null;
    resolvedForClientId: string | null;
    resolvedForClientPublicId: string | null;
    alreadyCaptured: boolean;
  };
  bookingContext: {
    candidateClientId: string | null;
    candidateClientPublicId: string | null;
    canonicalPlannedMoveInDate: string | null;
  };
  canResolveOrCreateClientOnConfirm: boolean;
  canBypassPublicIdentityForm: boolean;
  nextGate: 'identity_requirements' | 'crm_identity_conflict' | 'source_prompt' | 'move_in_date' | 'final_review' | string | null;
  missingRequirements: AuthenticatedBookingMissingRequirement[];
}

export interface UseAuthenticatedBookingReadinessOptions {
  enabled?: boolean;
  userId?: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function toBoolean(value: unknown): boolean {
  return value === true;
}

function toNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function parseMissingRequirements(value: unknown): AuthenticatedBookingMissingRequirement[] {
  if (!Array.isArray(value)) return [];

  return value.filter((item): item is AuthenticatedBookingMissingRequirement => typeof item === 'string');
}

function parseReadiness(value: unknown): AuthenticatedBookingReadiness | null {
  if (!isRecord(value) || value.success !== true) {
    return null;
  }

  const actorAccess = isRecord(value.actor_access) ? value.actor_access : {};
  const identity = isRecord(value.identity) ? value.identity : {};
  const crmIdentity = isRecord(value.crm_identity) ? value.crm_identity : {};
  const sourcePrompt = isRecord(value.source_prompt) ? value.source_prompt : {};
  const bookingContext = isRecord(value.booking_context) ? value.booking_context : {};

  return {
    actorAccess: {
      roleCode: toNullableString(actorAccess.role_code),
      accessStatus: toNullableString(actorAccess.access_status),
      canAccess: toBoolean(actorAccess.can_access),
      isPublicTenantActor: toBoolean(actorAccess.is_public_tenant_actor),
    },
    identity: {
      displayName: toNullableString(identity.display_name),
      canonicalEmail: toNullableString(identity.canonical_email),
      emailConfirmed: toBoolean(identity.email_confirmed),
      verifiedPhone: toNullableString(identity.verified_phone),
      bookingProfileSufficient: toBoolean(identity.booking_profile_sufficient),
    },
    crmIdentity: {
      candidateClientId: toNullableString(crmIdentity.candidate_client_id),
      candidateClientPublicId: toNullableString(crmIdentity.candidate_client_public_id),
      resolutionStatus: toNullableString(crmIdentity.resolution_status),
      resolutionStrategy: toNullableString(crmIdentity.resolution_strategy),
      activeLinkId: toNullableString(crmIdentity.active_link_id),
      matchedEmailClientId: toNullableString(crmIdentity.matched_email_client_id),
      matchedVerifiedPhoneClientId: toNullableString(crmIdentity.matched_verified_phone_client_id),
      matchedEmailCount: toNumber(crmIdentity.matched_email_count),
      matchedVerifiedPhoneCount: toNumber(crmIdentity.matched_verified_phone_count),
      canLinkDeterministically: toBoolean(crmIdentity.can_link_deterministically),
      canCreateOnConfirm: toBoolean(crmIdentity.can_create_on_confirm),
      isAmbiguous: toBoolean(crmIdentity.is_ambiguous),
    },
    sourcePrompt: {
      state: toNullableString(sourcePrompt.state),
      resolvedForClientId: toNullableString(sourcePrompt.resolved_for_client_id),
      resolvedForClientPublicId: toNullableString(sourcePrompt.resolved_for_client_public_id),
      alreadyCaptured: toBoolean(sourcePrompt.already_captured),
    },
    bookingContext: {
      candidateClientId: toNullableString(bookingContext.candidate_client_id),
      candidateClientPublicId: toNullableString(bookingContext.candidate_client_public_id),
      canonicalPlannedMoveInDate: toNullableString(bookingContext.canonical_planned_move_in_date),
    },
    canResolveOrCreateClientOnConfirm: toBoolean(value.can_resolve_or_create_client_on_confirm),
    canBypassPublicIdentityForm: toBoolean(value.can_bypass_public_identity_form),
    nextGate: toNullableString(value.next_gate),
    missingRequirements: parseMissingRequirements(value.missing_requirements),
  };
}

function normalizeReadinessError(error: PostgrestError | Error): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(error.message || 'No pudimos revisar el readiness autenticado para booking.');
}

async function getAuthenticatedBookingReadiness(): Promise<AuthenticatedBookingReadiness> {
  const { data, error } = await supabase.rpc('rpc_get_authenticated_booking_readiness_v1' as never);

  if (error) {
    throw normalizeReadinessError(error);
  }

  const readiness = parseReadiness(data);
  if (!readiness) {
    throw new Error('El readiness autenticado de booking devolvió un shape inesperado.');
  }

  return readiness;
}

export function useAuthenticatedBookingReadiness(
  options: UseAuthenticatedBookingReadinessOptions = {},
) {
  const enabled = options.enabled ?? true;

  return useQuery<AuthenticatedBookingReadiness, Error>({
    queryKey: authenticatedBookingReadinessKeys.detail(options.userId),
    queryFn: getAuthenticatedBookingReadiness,
    enabled,
    staleTime: 0,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}